import React, { FC } from 'react';
import styled from 'styled-components';
import { connect } from 'react-redux';
import { header6 } from '@westwing/ui-kit/typography';
import StateInterface from '../../../redux/types';
import { hideOverlay } from '../../../redux/overlays/actions';
import { getOverlayById } from '../../../redux/overlays/selectors';
import { CrossIcon, Direction } from '../../Generic/Icons/StyledIcons';
import ControlsArea from '../ControllsArea';
import ScrollableArea from '../ScrollableArea';
import ErrorBoundary from '../../Generic/ErrorBoundaries';
import GenericErrorRetry from '../../Generic/ErrorPages/GenericErrorRetry';

const StyledOverlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: ${({ theme }) => theme.colors.white};
    z-index: ${({ theme }) => theme.zindexCategoryNav};
`;

export const StyledCloseTitleContainer = styled.div`
    padding: 20px 15px;
    width: 100%;
    color: ${({ theme }) => theme.colors.charcoal100};

    display: grid;
    grid-template-columns: 25px 1fr 25px;
    align-items: center;
`;

export const StyledTitleContainer = styled.div`
    margin: auto;
    text-align: center;
    min-height: 26px;
    grid-row: 1 / span 1;
    grid-column: 2 / span 1;
    white-space: nowrap;
    ${header6}
    color: ${({ theme }) => theme.colors.charcoal100};
`;

const StyledStackedLayout = styled.div`
    display: flex;

    height: 100%;
    flex-direction: column;
    justify-content: space-between;
`;

const LineSeparator = styled.div`
    width: 100%;
    border-top: 2px solid ${({ theme }) => theme.colors.charcoal20};
`;

export const BaseButton = styled.button`
    width: 11px;
    height: 11px;
    padding-top: 8px;
    grid-row: 1 / span 1;
    cursor: pointer;
`;

export const RightButton = styled(BaseButton)`
    width: 100%;
    height: 100%;
    grid-column: 3 / span 1;
`;

export const LeftButton = styled(BaseButton)`
    grid-column: 1 / span 1;
`;

const StyledCrossIcon = styled(CrossIcon)`
    margin: auto;
`;
interface FlyoutProps {
    children: React.ReactElement | React.ReactElement[];
    identifier: string;
    show?: boolean;
    title?: string;
    hideOverlayEvent?: Function;
    cleanup?: () => void;
    hasCustomHeader?: boolean;
    closeBtnPosition?: Direction.Right | Direction.Left;
}

// Flyout component can have either one or two children.
// If there are two children, the first one is put in main content area
// and the second one - in a control area:
// |header|
// | main |
// | main |
// | main |
// |control|
const Flyout: FC<FlyoutProps> = ({
    children = null,
    show = false,
    identifier,
    title,
    hideOverlayEvent = () => {},
    cleanup = () => {},
    hasCustomHeader = false,
    closeBtnPosition,
}) => {
    if (!show) {
        return null;
    }

    const handleDismissClick = (): void => {
        hideOverlayEvent(identifier);
        cleanup();
    };

    let mainContent = children;
    let controlAreaContent = null;

    if (Array.isArray(children)) {
        [mainContent, controlAreaContent] = children;
    }

    const idLowerCase = identifier.toLocaleLowerCase();

    return (
        <>
            <ErrorBoundary boundaryName={`Flyout-${idLowerCase}`} fallbackComponent={<GenericErrorRetry />}>
                <StyledOverlay id={idLowerCase} data-testid={idLowerCase}>
                    <StyledStackedLayout>
                        <div>
                            {!hasCustomHeader && (
                                <StyledCloseTitleContainer>
                                    {closeBtnPosition === Direction.Right ? (
                                        <RightButton
                                            type="button"
                                            data-testid="flyout-dismiss-click"
                                            onClick={handleDismissClick}
                                        >
                                            <StyledCrossIcon />
                                        </RightButton>
                                    ) : (
                                        <LeftButton
                                            type="button"
                                            data-testid="flyout-dismiss-click"
                                            onClick={handleDismissClick}
                                        >
                                            <CrossIcon />
                                        </LeftButton>
                                    )}
                                    <StyledTitleContainer>{title}</StyledTitleContainer>
                                </StyledCloseTitleContainer>
                            )}
                        </div>
                        <ScrollableArea>{mainContent}</ScrollableArea>
                        {controlAreaContent && (
                            <ControlsArea>
                                <LineSeparator />
                                {controlAreaContent}
                            </ControlsArea>
                        )}
                    </StyledStackedLayout>
                </StyledOverlay>
            </ErrorBoundary>
        </>
    );
};

const mapStateToProps = (state: StateInterface, ownProps: FlyoutProps) => {
    const overlay = getOverlayById(state.overlays, ownProps.identifier);

    return { show: overlay.isActive, identifier: ownProps.identifier };
};

const mapDispatchToProps = (dispatch: Function): {} => ({
    hideOverlayEvent: (id: string): void => dispatch(hideOverlay(id)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Flyout);
